<template>
  <div>
    <div
      class="add-post card border-0 shadow-lg mx-auto mb-5"
      style="max-width: 600px; border-radius: 20px; overflow: hidden"
    >
      <div class="card-body p-4">
        <div class="d-flex align-items-center mb-3">
          <div
            class="avatar-sm bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mr-3 shadow-sm"
            style="width: 40px; height: 40px"
          >
            <i class="fas fa-pen-nib"></i>
          </div>
          <input
            v-model="postTitle"
            type="text"
            class="form-control border-0 no-focus-outline font-weight-bold"
            placeholder="Give your post a title..."
            style="font-size: 1.2rem"
          />
        </div>

        <textarea
          v-model="postContent"
          class="form-control border-0 no-focus-outline text-dark custom-scrollbar"
          placeholder="Share your thoughts with the world..."
          rows="3"
          style="resize: none; font-size: 1.05rem"
        ></textarea>

        <transition name="fade">
          <div
            v-if="imagePreview"
            class="position-relative mt-3 rounded-xl overflow-hidden shadow-md"
          >
            <img
              :src="imagePreview"
              class="img-fluid w-100"
              style="max-height: 350px; object-fit: cover"
            />
            <button
              @click="removeImage"
              class="btn btn-blur position-absolute shadow-sm"
              style="top: 15px; right: 15px"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </transition>
      </div>

      <div
        class="card-footer bg-light border-0 d-flex justify-content-between align-items-center px-4 py-3"
      >
        <div class="d-flex align-items-center gap-3">
          <label
            for="upload"
            class="action-icon text-primary mb-0 mr-3"
            v-tooltip="'Add Photo'"
          >
            <i class="fa-solid fa-circle-plus fa-2xl"></i>
          </label>
          <input
            type="file"
            id="upload"
            class="d-none"
            @change="handleFileChange"
            accept="image/*"
          />

          <!-- <select v-model="visibility" class="minimal-select">
          <option value="all">🌐 Public</option>
          <option value="group">👥 Group</option>
        </select> -->
        </div>

        <button
          @click="submitPost"
          :disabled="isSubmitting || !postTitle.trim()"
          class="btn btn-gradient px-4 py-2"
        >
          <span
            v-if="isSubmitting"
            class="spinner-border spinner-border-sm mr-2"
          ></span>
          {{ isSubmitting ? "Publishing..." : "Publish Post" }}
        </button>
      </div>
    </div>

    <div class="show-post">
      <transition-group name="list" tag="div">
        <div
          v-for="post in posts"
          :key="post.id"
          class="post-card card border-0 shadow-sm mb-4 mx-auto"
          :id="'post-' + post.id"
          style="max-width: 600px; border-radius: 20px"
        >
          <div
            class="card-header bg-transparent border-0 d-flex align-items-center p-4"
          >
            <div class="avatar-glow mr-3">
              <img
                :src="post.profile || 'https://via.placeholder.com/50'"
                class="rounded-circle shadow-sm"
                style="width: 50px; height: 50px; object-fit: cover"
                loading="lazy"
              />
            </div>
            <div class="flex-grow-1">
              <h6 class="mb-0 font-weight-bold text-dark-emphasis">
                {{ post.author }}
              </h6>
              <small class="text-muted d-flex align-items-center">
                <i class="far fa-clock mr-1"></i> {{ post.created_at }}
                <span class="mx-2">•</span>
                <i
                  :class="
                    post.visibility === 'all'
                      ? 'fas fa-globe-americas'
                      : 'fas fa-users'
                  "
                ></i>
              </small>
            </div>
            <div class="dropdown ml-auto" v-if="isMyPost(post.user_id)">
              <button
                class="btn btn-link text-muted p-0 no-caret"
                type="button"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <i
                  class="fas fa-ellipsis-h p-2 hover-bg-light rounded-circle transition"
                ></i>
              </button>
              <div
                class="dropdown-menu dropdown-menu-right shadow border-0 p-2"
                style="border-radius: 12px; min-width: 150px"
              >
                <button
                  class="dropdown-item rounded-lg py-2 mb-1"
                  @click="openEditModal(post)"
                >
                  <i class="far fa-edit mr-2 text-primary"></i>
                  <span class="small font-weight-bold">Edit Post</span>
                </button>
                <div class="dropdown-divider"></div>
                <button
                  class="dropdown-item rounded-lg py-2 text-danger"
                  @click="handleDeletePost(post.id)"
                >
                  <i class="far fa-trash-alt mr-2"></i>
                  <span class="small font-weight-bold">Delete Post</span>
                </button>
              </div>
            </div>
          </div>

          <div class="card-body px-4 pt-0">
            <h5 class="font-weight-bold mb-3">{{ post.title }}</h5>

            <p class="post-text text-secondary">
              <span v-if="!post.showFull && shouldShowMore(post)">
                {{ truncateText(post.content, 150) }}...
              </span>
              <span v-else>
                {{ post.content }}
              </span>

              <button
                v-if="shouldShowMore(post)"
                @click="post.showFull = !post.showFull"
                class="btn btn-link p-0 ml-1 font-weight-bold text-primary"
                style="text-decoration: none; font-size: 0.9rem"
              >
                {{ post.showFull ? "Show Less" : "Read More" }}
              </button>
            </p>

            <div
              v-if="post.image_url"
              class="post-media mt-3 rounded-lg overflow-hidden border shadow-sm"
              @click="openImage(post.image_url)"
            >
              <img
                :src="post.image_url"
                class="img-fluid w-100"
                loading="lazy"
              />
            </div>
          </div>

          <div class="card-footer bg-transparent border-top-0 px-4 pb-4">
            <div
              class="d-flex justify-content-around p-2 bg-light rounded-pill mb-3"
            >
              <button
                @click="handleLike(post)"
                class="btn btn-action w-50"
                :class="{ 'active-like': post.user_liked }"
              >
                <i
                  :class="
                    post.user_liked ? 'fas fa-heart scale-in' : 'far fa-heart'
                  "
                  class="mr-2"
                ></i>
                <span class="font-weight-bold">{{ post.likes_count }}</span>
              </button>
              <div class="divider-v"></div>
              <button @click="toggleComments(post)" class="btn btn-action w-50">
                <i class="far fa-comment-dots mr-2"></i>
                <span class="font-weight-bold">{{ post.comments.length }}</span>
              </button>
            </div>

            <transition name="slide-fade">
              <div v-if="post.showComments" class="comment-container mt-4">
                <div
                  v-for="comment in post.comments.filter((c) => !c.parent_id)"
                  :key="comment.id"
                  class="comment-group mb-3"
                >
                  <div
                    class="comment-bubble d-flex align-items-start group-comment"
                  >
                    <img
                      :src="comment.profile || 'https://via.placeholder.com/30'"
                      class="rounded-circle mr-2 shadow-sm"
                      style="width: 32px; height: 32px; object-fit: cover"
                    />
                    <div
                      class="bubble-content p-3 shadow-sm rounded-20 position-relative flex-grow-1 bg-white"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center mb-1"
                      >
                        <div class="font-weight-bold small text-primary">
                          {{ comment.author }}
                        </div>
                        <button
                          v-if="isMyComment(comment.user_id)"
                          @click="handleDeleteComment(post, comment.id)"
                          class="btn-delete-comment border-0 bg-transparent text-danger"
                        >
                          <i class="fa-regular fa-trash-can"></i>
                        </button>
                      </div>
                      <p class="mb-0 small text-dark">{{ comment.content }}</p>

                      <button
                        @click="showReplyInput(comment.id)"
                        class="btn btn-sm btn-link text-muted p-0 mt-1 small font-weight-bold"
                      >
                        Reply
                      </button>
                    </div>
                  </div>

                  <div
                    class="replies-container ml-5 mt-2 border-left pl-3"
                    style="border-left: 2px solid #f0f2f5 !important"
                  >
                    <div
                      v-for="reply in post.comments.filter(
                        (r) => r.parent_id === comment.id,
                      )"
                      :key="reply.id"
                      class="reply-bubble d-flex mb-2"
                    >
                      <img
                        :src="reply.profile || 'https://via.placeholder.com/25'"
                        class="rounded-circle mr-2"
                        style="width: 25px; height: 25px; object-fit: cover"
                      />
                      <div
                        class="bubble-content p-2 bg-light rounded-lg flex-grow-1 shadow-xs"
                      >
                        <div class="d-flex justify-content-between">
                          <div
                            class="font-weight-bold text-primary"
                            style="font-size: 0.75rem"
                          >
                            {{ reply.author }}
                          </div>
                          <button
                            v-if="isMyComment(reply.user_id)"
                            @click="handleDeleteComment(post, reply.id)"
                            class="btn-delete-comment border-0 bg-transparent text-danger p-0"
                            style="font-size: 0.7rem"
                          >
                            <i class="fa-regular fa-trash-can"></i>
                          </button>
                        </div>
                        <p class="mb-0 small text-secondary">
                          {{ reply.content }}
                        </p>
                      </div>
                    </div>

                    <div
                      v-if="activeReplyId === comment.id"
                      class="reply-input-wrapper mt-2 d-flex animate__animated animate__fadeIn"
                    >
                      <input
                        v-model="replyText"
                        @keyup.enter="submitReply(post, comment.id)"
                        class="form-control form-control-sm rounded-pill border-primary shadow-sm"
                        placeholder="Write a reply..."
                        autofocus
                      />
                      <button
                        @click="submitReply(post, comment.id)"
                        class="btn btn-primary btn-sm rounded-circle ml-2"
                      >
                        <i
                          class="fas fa-paper-plane"
                          style="font-size: 0.7rem"
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="input-wrapper mt-3">
                  <input
                    v-model="post.newComment"
                    @keyup.enter="submitComment(post)"
                    class="comment-input"
                    placeholder="Say something nice..."
                  />
                  <button @click="submitComment(post)" class="send-btn">
                    <i class="fas fa-paper-plane"></i>
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>
<script>
import { postsService } from "@/services/postService.js";
import { commentsService } from "@/services/commentService";
import Swal from "sweetalert2";

export default {
  name: "AddPostSaaS",
  data() {
    return {
      postTitle: "",
      postContent: "",
      visibility: "all",
      groupId: null,
      selectedFile: null,
      imagePreview: null,
      isSubmitting: false,
      posts: [],
      activeReplyId: null,
      replyText: "",
    };
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          return Swal.fire(
            "File Too Large",
            "Maximum image size is 5MB.",
            "error",
          );
        }
        this.selectedFile = file;
        const reader = new FileReader();
        reader.onload = (e) => (this.imagePreview = e.target.result);
        reader.readAsDataURL(file);
      }
    },
    removeImage() {
      this.selectedFile = null;
      this.imagePreview = null;
    },
    async submitPost() {
      if (!this.postTitle.trim() || !this.postContent.trim()) {
        return Swal.fire({
          icon: "warning",
          title: "Missing Info",
          text: "Every post needs a title and some content!",
          confirmButtonColor: "#3b82f6",
        });
      }
      this.isSubmitting = true;
      try {
        const formData = new FormData();
        formData.append("title", this.postTitle);
        formData.append("content", this.postContent);
        formData.append("visibility", this.visibility);
        if (this.visibility === "group")
          formData.append("group_id", this.groupId);
        if (this.selectedFile) formData.append("image", this.selectedFile);

        await postsService.createPost(formData);
        Swal.fire({
          icon: "success",
          title: "Published!",
          text: "Your post is now live.",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        this.resetForm();
        await this.getPosts();
      } catch (err) {
        Swal.fire(
          "Action Failed",
          err.message || "We couldn't publish your post.",
          "error",
        );
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.postTitle = "";
      this.postContent = "";
      this.visibility = "all";
      this.groupId = null;
      this.selectedFile = null;
      this.imagePreview = null;
    },
    async getPosts() {
      try {
        const res = await postsService.getAllPosts();
        this.posts = res
          ? res.map((post) => ({
              ...post,
              showFull: false,
              likes_count: post.likes_count || 0,
              user_liked: post.user_liked || false,
              showComments: false,
              newComment: "",
              comments: post.comments || [],
            }))
          : [];
        this.checkAndScrollToPost();
      } catch (err) {
        console.log("error : ", err);
      }
    },
    async handleLike(post) {
      try {
        const res = await postsService.toggleLike(post.id);

        if (res.liked) {
          post.likes_count++;
          post.user_liked = true;
        } else {
          post.likes_count--;
          post.user_liked = false;
        }
      } catch (err) {
        console.error("Error toggling like:", err);
      }
    },
    async submitComment(post) {
      if (!post.newComment.trim()) return;

      try {
        const commentData = {
          postId: post.id,
          content: post.newComment,
        };

        const savedComment = await commentsService.addComment(commentData);

        if (!post.comments) {
          this.$set(post, "comments", []);
        }

        post.comments.push(savedComment);
        post.newComment = "";
      } catch (err) {
        Swal.fire(
          "Error",
          "Could not save your comment. Please try again.",
          "error",
        );
        console.error(err);
      }
    },
    toggleComments(post) {
      post.showComments = !post.showComments;
    },
    shouldShowMore(post) {
      return post.content && post.content.length > 150;
    },
    truncateText(text, limit) {
      return text.substring(0, limit);
    },
    toggleContent(post) {
      post.showFull = !post.showFull;
    },
    handleImageError(event) {
      event.target.style.display = "none";
    },

    isMyComment(commentUserId) {
      const user = JSON.parse(localStorage.getItem("user"));
      return user && user.id === commentUserId;
    },

    async handleDeleteComment(post, commentId) {
      const result = await Swal.fire({
        title: "Delete comment?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#e74a3b",
        cancelButtonColor: "#858796",
        confirmButtonText: "Yes, delete it!",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        try {
          await commentsService.deleteComment(commentId);

          // تحديث الواجهة فوراً بحذف التعليق محلياً من المصفوفة
          post.comments = post.comments.filter((c) => c.id !== commentId);

          // تنبيه صغير بنجاح العملية
          const Toast = Swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2000,
          });
          Toast.fire({ icon: "success", title: "Deleted successfully" });
        } catch (err) {
          Swal.fire("Error", "Failed to delete comment", "error");
        }
      }
    },

    isMyPost(postUserId) {
      const user = JSON.parse(localStorage.getItem("user"));
      return user && user.id === postUserId;
    },

    async handleDeletePost(postId) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the post and all its comments.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#e74a3b",
        cancelButtonColor: "#858796",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          await postsService.deletePost(postId);
          this.posts = this.posts.filter((p) => p.id !== postId);
          Swal.fire("Deleted!", "Post has been removed.", "success");
        } catch (err) {
          Swal.fire("Error", "Could not delete post.", "error");
        }
      }
    },

    async openEditModal(post) {
      const { value: formValues } = await Swal.fire({
        title: "Edit Your Post",
        html: `
      <div class="text-left">
        <label class="small font-weight-bold">Title</label>
        <input id="swal-title" class="swal2-input w-100 m-0" placeholder="Title" value="${post.title}">
        
        <label class="small font-weight-bold mt-3">Content</label>
        <textarea id="swal-content" class="swal2-textarea w-100 m-0" placeholder="Content">${post.content}</textarea>
        
        <label class="small font-weight-bold mt-3 d-block">Change Image (Optional)</label>
        <input type="file" id="swal-file" class="form-control-file border p-1 rounded" accept="image/*">
      </div>
    `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: "Save Changes",
        preConfirm: () => {
          const title = document.getElementById("swal-title").value;
          const content = document.getElementById("swal-content").value;
          const file = document.getElementById("swal-file").files[0];

          if (!title.trim()) {
            Swal.showValidationMessage("Title is required");
            return false;
          }

          return { title, content, file };
        },
      });

      if (formValues) {
        try {
          // 1. تحويل البيانات إلى FormData لإرسال الملف والنص معاً
          const formData = new FormData();
          formData.append("title", formValues.title);
          formData.append("content", formValues.content);

          // نرسل الملف فقط إذا اختار المستخدم صورة جديدة
          if (formValues.file) {
            formData.append("image", formValues.file);
          }

          // 2. استدعاء الخدمة (تأكد أن السيرفر يستخدم Multer و Cloudinary)
          const response = await postsService.updatePost(post.id, formData);

          // 3. تحديث الواجهة فوراً
          post.title = formValues.title;
          post.content = formValues.content;

          // إذا أعاد السيرفر رابط الصورة الجديد من Cloudinary قم بتحديثه
          if (response && response.image_url) {
            post.image_url = response.image_url;
          }

          Swal.fire(
            "Updated!",
            "Your post has been edited successfully.",
            "success",
          );
        } catch (err) {
          console.error(err);
          Swal.fire("Error", "Update failed. Please try again.", "error");
        }
      }
    },

    showReplyInput(commentId) {
      this.activeReplyId = this.activeReplyId === commentId ? null : commentId;
      this.replyText = "";
    },

    async submitReply(post, parentId) {
      if (!this.replyText.trim()) return;

      try {
        const payload = {
          postId: post.id,
          content: this.replyText,
          parent_id: parentId,
        };

        const savedReply = await commentsService.addComment(payload);

        post.comments.push(savedReply);

        this.replyText = "";
        this.activeReplyId = null;

        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
        Toast.fire({ icon: "success", title: "Reply posted!" });
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Could not send your reply", "error");
      }
    },
    checkAndScrollToPost() {
      const hash = window.location.hash;
      if (hash && hash.startsWith("#post-")) {
        const postId = parseInt(hash.replace("#post-", ""));

        // 1. البحث عن المنشور وفتح التعليقات
        const targetPost = this.posts.find((p) => p.id === postId);
        if (targetPost) {
          targetPost.showComments = true; // فتح التعليقات أولاً

          // 2. الانتظار حتى يظهر قسم التعليقات في الـ DOM
          this.$nextTick(() => {
            // ننتظر قليلاً (مثلاً 600ms) حتى ينتهي تأثير الـ slide-fade للتعليقات
            setTimeout(() => {
              const element = document.getElementById(`post-${postId}`);
              if (element) {
                // التمرير إلى المنشور مع التأكد من أنه في أعلى الشاشة
                const yOffset = -100; // مسافة لإزاحة الـ Navbar
                const y =
                  element.getBoundingClientRect().top +
                  window.pageYOffset +
                  yOffset;

                window.scrollTo({ top: y, behavior: "smooth" });

                // إضافة تأثير التميز
                element.classList.add("highlight-post");
                setTimeout(
                  () => element.classList.remove("highlight-post"),
                  3000,
                );
              }
            }, 600);
          });
        }
      }
    },
  },
  async mounted() {
    await this.getPosts();
    this.checkAndScrollToPost();
  },
};
</script>

<style scoped lang="scss">
$primary: #4e73df;
$gradient: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
$bg-light: #f8f9fc;

.rounded-20 {
  border-radius: 20px;
}
.rounded-xl {
  border-radius: 15px;
}

.btn-gradient {
  background: $gradient;
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(78, 115, 223, 0.3);
  }
}

.btn-blur {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 5px 10px;
}

.btn-action {
  border-radius: 30px;
  color: #6e707e;
  padding: 8px;
  transition: all 0.2s;
  border: none;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  &.active-like {
    color: #e74a3b;
    .scale-in {
      animation: heartBeat 0.3s ease-in-out;
    }
  }
}

@keyframes heartBeat {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.4);
  }
  100% {
    transform: scale(1);
  }
}

.input-wrapper {
  position: relative;
  background: #f1f3f5;
  border-radius: 25px;
  padding: 5px;
  display: flex;
  align-items: center;

  .comment-input {
    background: transparent;
    border: none;
    padding: 10px 15px;
    width: 100%;
    outline: none;
    font-size: 0.9rem;
  }

  .send-btn {
    background: $primary;
    color: white;
    border: none;
    border-radius: 50%;
    width: 35px;
    height: 35px;
    transition: 0.3s;
    &:hover {
      transform: rotate(15deg) scale(1.1);
    }
  }
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}
.list-enter {
  opacity: 0;
  transform: translateY(30px);
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-enter {
  transform: translateY(-10px);
  opacity: 0;
}

.bubble-content {
  background: #ffffff;
  border-radius: 18px 18px 18px 2px;
  max-width: 85%;
}

.minimal-select {
  border: none;
  background: transparent;
  font-weight: 600;
  color: #4e73df;
  cursor: pointer;
  outline: none;
}

.group-comment {
  .btn-delete-comment {
    position: absolute;
    right: -10px;
    top: -5px;
    background: white;
    border: none;
    padding: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    cursor: pointer;
    z-index: 10;

    .icon-circle {
      color: #858796;
      font-size: 0.8rem;
      transition: color 0.2s;
    }

    &:hover {
      background: #fff5f5;
      transform: scale(1.1) translateY(-2px);
      box-shadow: 0 6px 15px rgba(231, 74, 59, 0.15);

      .icon-circle {
        color: #e74a3b;
        animation: shake 0.3s ease-in-out;
      }
    }
  }

  .no-caret::after {
    display: none;
  }

  .hover-bg-light:hover {
    background-color: #f8f9fa;
  }

  .dropdown-item {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f1f3f5;
      transform: translateX(3px);
    }

    &.text-danger:hover {
      background-color: #fff5f5;
    }
  }

  .swal2-input,
  .swal2-textarea {
    border-radius: 10px !important;
    font-size: 0.95rem !important;
  }

  &:hover .btn-delete-comment {
    opacity: 1;
    right: 8px;
    transform: scale(1);
  }
}

.post-card {
  scroll-margin-top: 500px;
  transition: all 0.5s ease;
}

.highlight-post {
  animation: highlight-animation 2s ease-out;
}

@keyframes highlight-animation {
  0% {
    box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.5);
    transform: scale(1.02);
  }
  100% {
    box-shadow: none;
    transform: scale(1);
  }
}
@keyframes shake {
  0%,
  100% {
    transform: rotate(0);
  }
  25% {
    transform: rotate(-15deg);
  }
  75% {
    transform: rotate(15deg);
  }
}
</style>
